import { useState } from 'react';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Avatar,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import { useAuthState } from '../../../../../contexts/AuthContext';
import {
  Visibility,
  VisibilityOff,
  CheckCircleOutline,
  ErrorOutline,
} from '@mui/icons-material';
import api from '../../../../../api/api';

const STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  AUTHORIZED: 'authorized',
  UNAUTHORIZED: 'unauthorized',
};

export default function DialogConfirmaSenha({
  open,
  handleClose,
  producaoSelecionada,
  maquinaAtual,
  atualizaFiltro,
  setAtualizaFiltro,
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState(STATUS.IDLE);
  const [errorMessage, setErrorMessage] = useState('');
  const { empresaAtiva } = useAuthState();
  async function handleSubmit(e) {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    setStatus(STATUS.LOADING);

    api
      .post(
        `/producao/liberar/${producaoSelecionada}/${maquinaAtual}`,
        {
          usuario: data.get('username'),
          senha: data.get('password'),
        },
        {
          headers: { banco: empresaAtiva },
        },
      )
      .then(() => {
        setStatus(STATUS.AUTHORIZED);
      })
      .catch((err) => {
        console.log(err);
        setErrorMessage(
          err.response?.data?.message ||
            'Credenciais inválidas ou sem permissão para esta operação.',
        );
        setStatus(STATUS.UNAUTHORIZED);
      });
  }
  function onClose() {
    if (status === STATUS.LOADING) return;
    setStatus(STATUS.IDLE);
    setErrorMessage('');
    setAtualizaFiltro(!atualizaFiltro);
    handleClose();
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      {/* Resultado: autorizado */}
      {status === STATUS.AUTHORIZED && (
        <>
          <DialogContent>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={1}
              py={2}
            >
              <Avatar sx={{ bgcolor: 'success.light', width: 52, height: 52 }}>
                <CheckCircleOutline color="success" fontSize="large" />
              </Avatar>
              <Typography variant="h6" fontWeight={500}>
                Acesso autorizado
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
              >
                Produção <strong>{producaoSelecionada}</strong> liberada com
                sucesso na máquina <strong>{maquinaAtual}</strong>.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={onClose}
              variant="contained"
              color="success"
              fullWidth
            >
              Continuar
            </Button>
          </DialogActions>
        </>
      )}

      {/* Formulário + estados idle/loading/unauthorized */}
      {status !== STATUS.AUTHORIZED && (
        <>
          <DialogTitle>Liberar produção</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Insira o usuário e senha para liberar a produção{' '}
              <strong>{producaoSelecionada}</strong> na máquina{' '}
              <strong>{maquinaAtual}</strong>.
            </DialogContentText>

            {status === STATUS.UNAUTHORIZED && (
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                mt={1.5}
                px={1.5}
                py={1}
                borderRadius={1}
                bgcolor="error.light"
              >
                <ErrorOutline fontSize="small" color="error" />
                <Typography variant="body2" color="primary">
                  {errorMessage}
                </Typography>
              </Box>
            )}

            <form onSubmit={handleSubmit} id="login-form">
              <TextField
                autoFocus
                required
                margin="dense"
                fullWidth
                variant="standard"
                id="username"
                name="username"
                label="Usuário"
                type="text"
                disabled={status === STATUS.LOADING}
              />
              <TextField
                required
                margin="dense"
                fullWidth
                variant="standard"
                id="password"
                name="password"
                label="Senha"
                type={showPassword ? 'text' : 'password'}
                disabled={status === STATUS.LOADING}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((s) => !s)}
                        edge="end"
                        disabled={status === STATUS.LOADING}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </form>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} disabled={status === STATUS.LOADING}>
              Cancelar
            </Button>
            <Button
              type="submit"
              form="login-form"
              variant="contained"
              disabled={status === STATUS.LOADING}
              startIcon={
                status === STATUS.LOADING ? (
                  <CircularProgress size={16} color="inherit" />
                ) : null
              }
            >
              {status === STATUS.LOADING ? 'Verificando...' : 'Liberar'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
